<script setup lang="ts">
import { Form, Head, router, setLayoutProps } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';
import { ArrowRight, Fingerprint, Mail } from 'lucide-vue-next';
import { usePasskeyVerify } from '@laravel/passkeys/vue';
import InputError from '@/components/InputError.vue';
import PasswordInput from '@/components/PasswordInput.vue';
import TextLink from '@/components/TextLink.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { register } from '@/routes';
import { store } from '@/routes/login';
import { request } from '@/routes/password';

const { t } = useI18n();

setLayoutProps({
    panelEyebrow: t('auth.panel.welcomeBack'),
    panelHeadline: t('auth.panel.loginHeadline'),
    panelSub: t('auth.panel.loginSub'),
});

const props = defineProps<{
    status?: string;
    suspended?: string;
    canResetPassword: boolean;
    canRegister: boolean;
    canUsePasskeys?: boolean;
}>();

const { verify, isLoading: passkeyLoading, isSupported: passkeySupported } = usePasskeyVerify({
    onSuccess: (response) => {
        if (response.redirect) {
            router.visit(response.redirect);
        }
    },
});
</script>

<template>
    <div>
        <Head :title="t('auth.login.title')" />

        <div v-if="suspended" role="alert" class="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-center text-[13px] font-medium text-destructive">
            {{ suspended }}
        </div>

        <div v-if="status" role="status" aria-live="polite" class="mb-4 rounded-md bg-nv-success/10 px-3 py-2 text-center text-[13px] font-medium text-nv-success">
            {{ status }}
        </div>

        <h1 class="text-[26px] font-semibold tracking-tight text-foreground">{{ t('auth.login.layoutTitle') }}</h1>
        <p class="mt-1.5 mb-6 text-[13px] text-muted-foreground">{{ t('auth.login.layoutDescription') }}</p>

        <Form
            v-bind="store.form()"
            :reset-on-success="['password']"
            v-slot="{ errors, processing }"
            class="flex flex-col"
        >
            <!-- Email -->
            <div class="mb-3">
                <Label for="email" class="mb-1.5 text-[12px]">{{ t('auth.login.email') }}</Label>
                <div class="relative">
                    <Mail class="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" :stroke-width="1.6" aria-hidden="true" />
                    <Input
                        id="email"
                        type="email"
                        name="email"
                        required
                        autofocus
                        :tabindex="1"
                        autocomplete="email"
                        placeholder="email@example.com"
                        class="pl-9"
                        :aria-invalid="!!errors.email"
                        :aria-describedby="errors.email ? 'email-error' : undefined"
                    />
                </div>
                <InputError :message="errors.email" id="email-error" />
            </div>

            <!-- Password -->
            <div class="mb-3">
                <div class="mb-1.5 flex items-center">
                    <Label for="password" class="text-[12px]">{{ t('auth.login.password') }}</Label>
                    <TextLink
                        v-if="canResetPassword"
                        :href="request()"
                        class="ml-auto text-[11px] text-nv-indigo"
                        :tabindex="5"
                    >
                        {{ t('auth.login.forgotPassword') }}
                    </TextLink>
                </div>
                <PasswordInput
                    id="password"
                    name="password"
                    required
                    :tabindex="2"
                    autocomplete="current-password"
                    placeholder="Password"
                    :aria-invalid="!!errors.password"
                    :aria-describedby="errors.password ? 'password-error' : undefined"
                />
                <InputError :message="errors.password" id="password-error" />
            </div>

            <!-- Remember me -->
            <Label for="remember" class="mb-5 flex cursor-pointer items-center gap-2 text-[12px] text-nv-ink2">
                <Checkbox id="remember" name="remember" :tabindex="3" />
                <span>{{ t('auth.login.rememberMe') }}</span>
            </Label>

            <!-- Submit -->
            <Button
                type="submit"
                class="w-full justify-center"
                :tabindex="4"
                :disabled="processing"
                data-test="login-button"
            >
                <Spinner v-if="processing" />
                {{ t('auth.login.submit') }}
                <ArrowRight v-if="!processing" class="ml-1.5 size-4" :stroke-width="1.6" aria-hidden="true" />
            </Button>
        </Form>

        <!-- Passkey login -->
        <template v-if="props.canUsePasskeys && passkeySupported">
            <div class="relative my-5 flex items-center" role="separator">
                <div class="flex-1 border-t border-border" aria-hidden="true"></div>
                <span class="px-3 text-[11px] text-muted-foreground">{{ t('auth.login.or') }}</span>
                <div class="flex-1 border-t border-border" aria-hidden="true"></div>
            </div>

            <Button
                variant="outline"
                class="w-full justify-center"
                :disabled="passkeyLoading"
                @click="verify"
            >
                <Spinner v-if="passkeyLoading" />
                <Fingerprint v-else class="mr-1.5 size-4" :stroke-width="1.6" aria-hidden="true" />
                {{ t('auth.login.passkey') }}
            </Button>
        </template>

        <!-- Register link -->
        <div v-if="canRegister" class="mt-6 text-center text-[13px] text-muted-foreground">
            {{ t('auth.login.noAccount') }}
            {{ ' ' }}
            <TextLink :href="register()" class="font-medium text-foreground" :tabindex="6">{{ t('auth.login.signUp') }} &rarr;</TextLink>
        </div>
    </div>
</template>
